/* eslint-disable no-magic-numbers */
import {
  Col,
  Dropdown,
  Flex,
  Pagination,
  PaginationProps,
  Row,
  Space,
} from 'antd';

import { ArrowLeft, ArrowRight, CaretDownDark } from '../../icons';
import { handleKeyboardClick } from '../../utils';

import './TablePagination.scss';
import { getPageSizeOptions, getShowingRange } from './table-utils';

/**
 * define the props type of table pagination
 */
export interface TablePaginationProps {
  page: number;
  pageSize: number;
  totalEntries: number;
  isLoading: boolean;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  manualPagination?: boolean;
  totalRecord: number;
  pageSizeOptions?: number[];
  hideOnPrint?: boolean;
}

const defaultPageSizeOptions = [10, 25, 50, 100];

/**
 * returns pagination icon
 */
const itemRender: PaginationProps['itemRender'] = (
  _,
  type,
  originalElement,
) => {
  if (type === 'prev') {
    return <ArrowLeft />;
  }
  if (type === 'next') {
    return <ArrowRight />;
  }
  return originalElement;
};

/**
 * returns JSX for table pagination
 */
export function TablePagination({
  page,
  pageSize,
  totalEntries,
  isLoading,
  setPage,
  setPageSize,
  manualPagination = false,
  totalRecord,
  pageSizeOptions = defaultPageSizeOptions,
  hideOnPrint = false,
}: Readonly<TablePaginationProps>) {
  if (totalEntries === 0) {
    return null;
  }

  const scrollToNavigation = () => {
    if (typeof window === 'undefined') return; // Guard for SSR/test environments
    const currentScrollY = window.scrollY; // Get current scroll position
    setTimeout(() => {
      if (typeof window === 'undefined') return; // Guard for test environments
      window.scrollTo({ top: currentScrollY, behavior: 'smooth' }); // Restore scroll position
    }, 0);
  };

  const items = getPageSizeOptions(
    pageSizeOptions,
    totalEntries,
    manualPagination,
  ).map((key) => ({
    key,
    label: `${key} rows/page`,
    onClick: () => {
      setPageSize(key);
      setTimeout(() => {
        scrollToNavigation();
      }, 300);
    },
  }));

  const { from, to } = getShowingRange(
    page,
    pageSize,
    totalEntries,
    !!items.length,
  );

  return (
    <Row className="suntex-table-pagination">
      <Col className="suntex-table-pagination--label">
        {isLoading ? (
          <>isLoading...</>
        ) : (
          <>
            Showing <strong>{from} </strong>
            to
            <strong> {to} </strong>
            of
            <strong> {totalEntries} </strong>entries
            {totalRecord > totalEntries && (
              <span> (filtered from {totalRecord} entries)</span>
            )}
          </>
        )}
      </Col>
      <Col className="suntex-table-pagination--numbers">
        <Flex
          gap="8px"
          align="center"
          justify="center"
          wrap="wrap"
          className={hideOnPrint ? 'print-hidden' : 'export-btn'}
        >
          {items.length ? (
            <Dropdown
              menu={{ items }}
              trigger={['click']}
              className="secondary"
            >
              <Space
                aria-label="Rows per page selection"
                tabIndex={0}
                role="button"
                onKeyDown={(e) => {
                  handleKeyboardClick(e);
                }}
              >
                {pageSize} rows/page
                <CaretDownDark />
              </Space>
            </Dropdown>
          ) : null}
          <Pagination
            current={page}
            pageSize={pageSize}
            onChange={(newPage) => {
              setPage(newPage);
              setTimeout(() => {
                scrollToNavigation();
              }, 300);
            }}
            total={totalEntries}
            itemRender={itemRender}
            onShowSizeChange={(newPage, newSize) => {
              setPage(newPage);
              setPageSize(newSize);
              setTimeout(() => {
                scrollToNavigation();
              }, 300);
            }}
            showSizeChanger={false}
          />
        </Flex>
      </Col>
    </Row>
  );
}
